import React, {useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react'
import {useFormikContext} from 'formik'
import {useQuery} from 'react-query'
import ReactDatePicker, {registerLocale} from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import ru from 'date-fns/locale/ru'
import moment from 'moment'
import api from '../../../api'
import useMutate from '../../../api/hooks/useMutate'
import {getSuccessToast} from '../../helpers/toasts'
import './_styles.scss'

registerLocale('ru', ru)

type TMode = 'date' | 'datetime' | 'booking'

type TScheduleBlock = {
    steps: [number, number]
    status: 'empty' | 'available' | 'busy'
}

type TPerformerDay = {
    performer_id: number
    cabinet_id?: number
    schedule: TScheduleBlock[]
}

type TApiScheduleData = {
    steps_list: string[]
    schedule: Record<string, Record<string, TPerformerDay>>
}

type TDropdownPos = {top: number; left: number; minWidth: number; openUp: boolean}

type TProps = {
    article: string
    is_disabled?: boolean
    settings?: {
        mode?: TMode
        doctor_field?: string
        service_field?: string
        /** @deprecated use service_field */
        service_article?: string
        client_field?: string
        store_field?: string
        is_by_direction?: boolean
        referring_user_field?: string
        referring_user_root_field?: string
        extra_fields?: string[]
    }
}

const STRIP_RADIUS = 3
const STRIP_SIZE = STRIP_RADIUS * 2 + 1

const RU_DAY = ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб']
const RU_MONTH = ['Январь','Февраль','Март','Апрель','Май','Июнь','Июль','Август','Сентябрь','Октябрь','Ноябрь','Декабрь']

const getNestedValue = (obj: any, path: string): any =>
    path.split(/[\.\[\]]+/).filter(Boolean).reduce((acc, key) => acc?.[key], obj)

const getParentPath = (article: string): string | null => {
    const idx = article.lastIndexOf('.')
    return idx !== -1 ? article.slice(0, idx) : null
}

const resolveId = (raw: any): number | undefined => {
    if (!raw) return undefined
    const val = Array.isArray(raw) ? raw[0] : raw
    if (!val) return undefined
    const num = Number(typeof val === 'object' && val !== null ? val.value ?? val.id : val)
    return Number.isFinite(num) && num > 0 ? num : undefined
}

const ComponentScheduleBookingDate: React.FC<TProps> = ({article, is_disabled, settings = {}}) => {
    const {
        mode = 'booking',
        doctor_field = 'user_id',
        service_field,
        service_article,
        client_field = 'clients_id',
        store_field = 'store_id',
        is_by_direction,
        referring_user_field,
        referring_user_root_field,
        extra_fields = [] as string[],
    } = settings

    const resolvedServiceField = service_field ?? service_article

    const {values, setFieldValue} = useFormikContext<any>()

    const parentPath = useMemo(() => getParentPath(article), [article])

    const doctorId: number | undefined = useMemo(() => {
        const raw = parentPath
            ? getNestedValue(values, `${parentPath}.${doctor_field}`)
            : getNestedValue(values, doctor_field)
        return resolveId(raw)
    }, [values, parentPath, doctor_field])

    const presetServiceId: number | undefined = useMemo(() => {
        if (!resolvedServiceField) return undefined
        const raw = parentPath
            ? getNestedValue(values, `${parentPath}.${resolvedServiceField}`)
            : getNestedValue(values, resolvedServiceField)
        return resolveId(raw)
    }, [values, parentPath, resolvedServiceField])

    const clientId = useMemo(() => {
        const raw = getNestedValue(values, client_field)
        if (Array.isArray(raw)) return raw
        return raw != null ? [raw] : []
    }, [values, client_field])

    const storeId: number | undefined = useMemo(() => {
        const raw = parentPath
            ? getNestedValue(values, `${parentPath}.${store_field}`)
            : getNestedValue(values, store_field)
        return resolveId(raw)
    }, [values, parentPath, store_field])

    const referringUserId: number | undefined = useMemo(() => {
        if (referring_user_root_field) return resolveId(getNestedValue(values, referring_user_root_field))
        if (!referring_user_field) return undefined
        const raw = parentPath
            ? getNestedValue(values, `${parentPath}.${referring_user_field}`)
            : getNestedValue(values, referring_user_field)
        return resolveId(raw)
    }, [values, parentPath, referring_user_field, referring_user_root_field])
    const currentValue: string | undefined = useMemo(() => getNestedValue(values, article), [values, article])

    const [calOpen, setCalOpen] = useState(false)
    const [viewMonth, setViewMonth] = useState<Date>(() => new Date())
    const [dropPos, setDropPos] = useState<TDropdownPos | null>(null)
    const inputRef = useRef<HTMLInputElement>(null)
    const calContainerRef = useRef<HTMLDivElement>(null)

    const [modalOpen, setModalOpen] = useState(false)
    const [pickedDate, setPickedDate] = useState<string | null>(null)
    const [stripCenter, setStripCenter] = useState<string | null>(null)
    const [selected, setSelected] = useState<{date: string; time: string} | null>(null)
    const [selectedServiceId, setSelectedServiceId] = useState<number | undefined>(undefined)
    const [stripAnimKey, setStripAnimKey] = useState(0)
    const [stripDir, setStripDir] = useState<'left' | 'right'>('left')
    const [dragOffset, setDragOffset] = useState(0)

    const bodyRef = useRef<HTMLDivElement>(null)
    const dragStartXRef = useRef<number | null>(null)
    const wheelAccRef = useRef(0)
    const wheelTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const DRAG_THRESHOLD = 60

    const fetchSchedule = useCallback(
        (start: string) =>
            api<any>('visits', 'schedule', {
                performers_article: 'user_id',
                performers_table: 'users',
                performers_title: 'first_name',
                user_id: doctorId,
                store_id: storeId,
                start_at: start,
            }).then((r: any) => r.data as TApiScheduleData),
        [doctorId, storeId]
    )

    const calFetchStart = useMemo(
        () => moment(viewMonth).startOf('month').format('YYYY-MM-DD'),
        [viewMonth]
    )

    const {data: calData, isFetching: calFetching} = useQuery<TApiScheduleData>(
        ['sbwSchedule', doctorId, storeId, calFetchStart],
        () => fetchSchedule(calFetchStart),
        {enabled: Boolean(doctorId) && calOpen, retry: false, refetchOnWindowFocus: false, staleTime: 60_000, keepPreviousData: true}
    )

    const modalFetchStart = useMemo(
        () => moment(stripCenter ?? pickedDate).startOf('month').format('YYYY-MM-DD'),
        [stripCenter, pickedDate]
    )

    const {data: modalData, isFetching: modalFetching} = useQuery<TApiScheduleData>(
        ['sbwSchedule', doctorId, storeId, modalFetchStart],
        () => fetchSchedule(modalFetchStart),
        {enabled: Boolean(doctorId) && modalOpen, retry: false, refetchOnWindowFocus: false, staleTime: 60_000, keepPreviousData: true}
    )

    // Services for booking mode without preset service
    const needServicePicker = mode === 'booking' && !presetServiceId
    const {data: servicesData} = useQuery<Array<{id: number; title: string}>>(
        ['sbwServices', doctorId],
        () => api<any>('services', 'get', {users_id: doctorId}).then((r: any) => r.data ?? []),
        {enabled: Boolean(doctorId) && needServicePicker && modalOpen && Boolean(selected), retry: false, refetchOnWindowFocus: false, staleTime: 120_000}
    )

    const workingDays = useMemo(
        () => new Set(Object.keys(calData?.schedule ?? {})),
        [calData]
    )

    const getDayClass = useCallback(
        (date: Date) => workingDays.has(moment(date).format('YYYY-MM-DD')) ? 'sbw__workDay' : '',
        [workingDays]
    )

    useLayoutEffect(() => {
        if (!calOpen || !inputRef.current) return
        const rect = inputRef.current.getBoundingClientRect()
        const spaceBelow = window.innerHeight - rect.bottom
        const spaceAbove = rect.top
        const openUp = spaceBelow < 320 && spaceAbove > spaceBelow
        const left = Math.min(rect.left, window.innerWidth - 280 - 8)
        setDropPos({top: openUp ? rect.top : rect.bottom + 4, left: Math.max(8, left), minWidth: 0, openUp})
    }, [calOpen])

    useEffect(() => {
        if (!calOpen) return
        const handler = (e: MouseEvent) => {
            if (calContainerRef.current && !calContainerRef.current.contains(e.target as Node)) {
                setCalOpen(false)
            }
        }
        document.addEventListener('mousedown', handler)
        return () => document.removeEventListener('mousedown', handler)
    }, [calOpen])

    // Sync preset service into local state
    useEffect(() => {
        if (presetServiceId) setSelectedServiceId(presetServiceId)
    }, [presetServiceId])

    const handleDatePick = useCallback((date: Date | null) => {
        if (!date) return
        const str = moment(date).format('YYYY-MM-DD')

        if (mode === 'date') {
            setFieldValue(article, str)
            setCalOpen(false)
            return
        }

        setPickedDate(str)
        setStripCenter(str)
        setSelected(null)
        setSelectedServiceId(presetServiceId)
        setCalOpen(false)
        setModalOpen(true)
    }, [mode, article, setFieldValue, presetServiceId])

    const shiftStrip = useCallback((dir: -1 | 1) => {
        setStripDir(dir === 1 ? 'left' : 'right')
        setStripAnimKey(k => k + 1)
        setStripCenter(c => moment(c ?? pickedDate).add(dir * STRIP_SIZE, 'days').format('YYYY-MM-DD'))
        setSelected(null)
    }, [pickedDate])

    const handleDayClick = useCallback((date: string, currentCenter: string) => {
        const dir = date > currentCenter ? 'left' : 'right'
        setStripDir(dir)
        setStripAnimKey(k => k + 1)
        setStripCenter(date)
        setSelected(null)
    }, [])

    const triggerShift = useCallback((dir: -1 | 1) => {
        setStripDir(dir === 1 ? 'left' : 'right')
        setStripAnimKey(k => k + 1)
        setStripCenter(c => moment(c ?? pickedDate).add(dir, 'days').format('YYYY-MM-DD'))
        setSelected(null)
        setDragOffset(0)
    }, [pickedDate])

    const handleBodyMouseDown = useCallback((e: React.MouseEvent) => {
        dragStartXRef.current = e.clientX
    }, [])

    const handleBodyMouseMove = useCallback((e: React.MouseEvent) => {
        if (dragStartXRef.current === null) return
        setDragOffset(e.clientX - dragStartXRef.current)
    }, [])

    const handleBodyMouseUp = useCallback((e: React.MouseEvent) => {
        if (dragStartXRef.current === null) return
        const delta = e.clientX - dragStartXRef.current
        dragStartXRef.current = null
        if (Math.abs(delta) >= DRAG_THRESHOLD) {
            triggerShift(delta < 0 ? 1 : -1)
        } else {
            setDragOffset(0)
        }
    }, [triggerShift])

    const handleBodyMouseLeave = useCallback(() => {
        if (dragStartXRef.current !== null) {
            dragStartXRef.current = null
            setDragOffset(0)
        }
    }, [])

    useEffect(() => {
        const el = bodyRef.current
        if (!el) return
        const onWheel = (e: WheelEvent) => {
            if (Math.abs(e.deltaX) < Math.abs(e.deltaY)) return
            e.preventDefault()
            wheelAccRef.current += e.deltaX
            if (wheelTimerRef.current) clearTimeout(wheelTimerRef.current)
            if (Math.abs(wheelAccRef.current) >= DRAG_THRESHOLD) {
                const dir = wheelAccRef.current > 0 ? 1 : -1
                wheelAccRef.current = 0
                triggerShift(dir)
            } else {
                wheelTimerRef.current = setTimeout(() => { wheelAccRef.current = 0 }, 300)
            }
        }
        el.addEventListener('wheel', onWheel, {passive: false})
        return () => el.removeEventListener('wheel', onWheel)
    }, [modalOpen, triggerShift])

    const stripDays = useMemo(() => {
        const center = stripCenter ?? pickedDate
        if (!center) return []
        return Array.from({length: STRIP_SIZE}, (_, i) => {
            const d = moment(center).subtract(STRIP_RADIUS, 'days').add(i, 'days')
            const date = d.format('YYYY-MM-DD')
            return {
                date,
                dayName: RU_DAY[d.day()],
                dayNum: d.date(),
                isActive: date === center,
            }
        })
    }, [pickedDate, stripCenter])

    const modalMonth = useMemo(() => {
        const center = stripCenter ?? pickedDate
        return center ? RU_MONTH[moment(center).month()] : ''
    }, [stripCenter, pickedDate])

    const getAvailableSteps = useCallback(
        (date: string): number[] => {
            if (!modalData) return []
            const dayData = modalData.schedule[date]
            if (!dayData) return []
            const result: number[] = []
            for (const key of Object.keys(dayData)) {
                const perf = dayData[key]
                if (perf.performer_id !== doctorId) continue
                for (const block of perf.schedule) {
                    if (block.status !== 'available') continue
                    for (let i = block.steps[0]; i <= block.steps[1]; i++) {
                        if (!result.includes(i)) result.push(i)
                    }
                }
            }
            return result.sort((a, b) => a - b)
        },
        [modalData, doctorId]
    )

    const availablePerDay = useMemo(() => {
        const map: Record<string, number[]> = {}
        for (const day of stripDays) map[day.date] = getAvailableSteps(day.date)
        return map
    }, [stripDays, getAvailableSteps])

    const timeRows = useMemo(() => {
        const seen: number[] = []
        for (const steps of Object.values(availablePerDay)) {
            for (const i of steps) if (!seen.includes(i)) seen.push(i)
        }
        return seen.sort((a, b) => a - b)
    }, [availablePerDay])

    const canConfirm = useMemo(() => {
        if (!selected) return false
        if (mode === 'booking' && !selectedServiceId) return false
        return true
    }, [selected, mode, selectedServiceId])

    const {mutateAsync: createVisit, isLoading: booking} = useMutate('visits', 'add', {success: false, error: true})

    const closeModal = useCallback(() => {
        setModalOpen(false)
        setSelected(null)
        setPickedDate(null)
    }, [])

    const handleDateOnly = useCallback(() => {
        if (!pickedDate) return
        setFieldValue(article, pickedDate)
        setModalOpen(false)
        setSelected(null)
        setPickedDate(null)
    }, [pickedDate, article, setFieldValue])

    const handleConfirm = useCallback(async () => {
        if (!selected) return
        const {date, time} = selected
        const dateTime = `${date} ${time}`

        if (mode === 'datetime') {
            setFieldValue(article, dateTime)
            setModalOpen(false)
            setSelected(null)
            setPickedDate(null)
            return
        }

        if (mode === 'booking') {
            if (!doctorId) return
            const normalizeClient = (c: any): number | null => {
                if (!c) return null
                if (typeof c === 'object') return Number(c.id ?? c.value) || null
                return Number(c) || null
            }
            const resolvedClient = (Array.isArray(clientId) ? clientId : [clientId])
                .map(normalizeClient)
                .filter((v): v is number => v !== null)
            try {
                const resolveFieldValue = (val: any): any => {
                    if (Array.isArray(val)) return val.map((v) => (v && typeof v === 'object' && 'value' in v ? v.value : v))
                    if (val && typeof val === 'object' && 'value' in val) return val.value
                    return val
                }
                const extraData = extra_fields.reduce((acc: Record<string, any>, fieldArticle: string) => {
                    if (values[fieldArticle] !== undefined) acc[fieldArticle] = resolveFieldValue(values[fieldArticle])
                    return acc
                }, {})
                await createVisit({
                    user_id: doctorId,
                    clients_id: resolvedClient,
                    ...(selectedServiceId ? {services_id: [selectedServiceId]} : {}),
                    ...(storeId ? {store_id: storeId} : {}),
                    start_at: `${date} ${time}:00`,
                    end_at: `${date} ${time}:00`,
                    visit_type: 'visit',
                    ...(is_by_direction ? {is_by_direction: true} : {}),
                    ...(is_by_direction && referringUserId ? {referring_user_id: referringUserId} : {}),
                    ...extraData,
                    context: {from_schedule: true},
                } as any)
                setFieldValue(article, dateTime)
                getSuccessToast()
                setModalOpen(false)
                setSelected(null)
                setPickedDate(null)
            } catch {}
        }
    }, [selected, mode, article, doctorId, clientId, selectedServiceId, storeId, is_by_direction, referringUserId, createVisit, setFieldValue])

    const displayValue = useMemo(() => {
        if (!currentValue) return ''
        if (currentValue.includes(' ')) return moment(currentValue).format('DD.MM.YYYY HH:mm')
        return moment(currentValue).format('DD.MM.YYYY')
    }, [currentValue])

    const selectedDateObj = useMemo(() => {
        if (!pickedDate) return null
        const [y, m, d] = pickedDate.split('-').map(Number)
        return new Date(y, m - 1, d)
    }, [pickedDate])

    return (
        <>
            <div className='sbw' ref={calContainerRef}>
                <input
                    ref={inputRef}
                    type='text'
                    readOnly
                    disabled={Boolean(is_disabled)}
                    className='sbw__input form-control form-control-solid'
                    value={displayValue}
                    placeholder='Выберите дату'
                    title={!doctorId ? 'Сначала выберите специалиста' : undefined}
                    style={!doctorId ? {cursor: 'not-allowed'} : undefined}
                    onClick={() => !is_disabled && doctorId && setCalOpen(v => !v)}
                />

                {calOpen && dropPos && (
                    <div
                        className={`sbw__dropdown${dropPos.openUp ? ' sbw__dropdown--up' : ''}`}
                        style={{
                            position: 'fixed',
                            top: dropPos.openUp ? undefined : dropPos.top,
                            bottom: dropPos.openUp ? window.innerHeight - dropPos.top : undefined,
                            left: dropPos.left,
                            minWidth: dropPos.minWidth,
                            zIndex: 9999,
                        }}
                    >
                        {!doctorId ? (
                            <div className='sbw__empty'>Сначала выберите специалиста</div>
                        ) : (
                            <div className='sbw__calWrap'>
                                {calFetching && <div className='sbw__calLoader'><span className='sbw__spinner' /></div>}
                                <ReactDatePicker
                                    inline
                                    locale='ru'
                                    selected={selectedDateObj}
                                    onChange={handleDatePick}
                                    onMonthChange={d => setViewMonth(d)}
                                    dayClassName={getDayClass}
                                    showMonthDropdown
                                    showYearDropdown
                                    dropdownMode='select'
                                />
                            </div>
                        )}
                    </div>
                )}
            </div>

            {modalOpen && (
                <div className='sbw__overlay' onClick={closeModal}>
                    <div className='sbw__modal' onClick={e => e.stopPropagation()}>
                        <div className='sbw__modalHead'>
                            <div className='sbw__modalHeadLeft'>
                                <button
                                    type='button'
                                    className='sbw__backBtn'
                                    onClick={() => { closeModal(); setCalOpen(true) }}
                                >
                                    ‹
                                </button>
                                <span className='sbw__modalTitle'>Время визита</span>
                            </div>
                            <span className='sbw__modalMonth'>{modalMonth}</span>
                        </div>

                        <div className='sbw__strip'>
                            <button type='button' className='sbw__nav' onClick={() => shiftStrip(-1)}>‹</button>
                            <div
                                key={stripAnimKey}
                                className={`sbw__stripDays sbw__stripDays--slide-${stripDir}`}
                            >
                                {stripDays.map(day => (
                                    <button
                                        key={day.date}
                                        type='button'
                                        className={`sbw__day${day.isActive ? ' sbw__day--active' : ''}`}
                                        onClick={() => handleDayClick(day.date, stripCenter ?? pickedDate ?? day.date)}
                                    >
                                        <span className='sbw__dayName'>{day.dayName}</span>
                                        <span className='sbw__dayNum'>{day.dayNum}</span>
                                    </button>
                                ))}
                            </div>
                            <button type='button' className='sbw__nav' onClick={() => shiftStrip(1)}>›</button>
                        </div>

                        <div
                            ref={bodyRef}
                            className='sbw__body'
                            onMouseDown={handleBodyMouseDown}
                            onMouseMove={handleBodyMouseMove}
                            onMouseUp={handleBodyMouseUp}
                            onMouseLeave={handleBodyMouseLeave}
                            style={{
                                cursor: dragOffset !== 0 ? 'grabbing' : 'grab',
                                transform: dragOffset ? `translateX(${dragOffset}px)` : undefined,
                                transition: dragOffset === 0 ? 'transform 0.15s ease' : 'none',
                            }}
                        >
                            {modalFetching ? (
                                <div className='sbw__state sbw__state--loading'>
                                    <span className='sbw__spinner' />
                                    Загрузка расписания...
                                </div>
                            ) : timeRows.length === 0 ? (
                                <div className='sbw__state'>Нет доступного времени на выбранную дату</div>
                            ) : (
                                <div
                                    key={stripAnimKey}
                                    className={`sbw__grid sbw__grid--slide-${stripDir}`}
                                >
                                    {timeRows.map(stepIdx => {
                                        const time = modalData!.steps_list[stepIdx]
                                        return (
                                            <div key={stepIdx} className='sbw__row'>
                                                {stripDays.map(day => {
                                                    const avail = availablePerDay[day.date]?.includes(stepIdx)
                                                    const isSel = selected?.date === day.date && selected?.time === time
                                                    if (!avail) return <div key={day.date} className='sbw__cell sbw__cell--empty' />
                                                    return (
                                                        <button
                                                            key={day.date}
                                                            type='button'
                                                            className={`sbw__cell${isSel ? ' sbw__cell--selected' : ''}`}
                                                            onClick={() => setSelected({date: day.date, time})}
                                                        >
                                                            {time}
                                                        </button>
                                                    )
                                                })}
                                            </div>
                                        )
                                    })}
                                </div>
                            )}
                        </div>

                        {/* Service picker — only when service not preset */}
                        {selected && needServicePicker && (
                            <div className='sbw__serviceWrap'>
                                <select
                                    className='sbw__serviceSelect form-select form-select-solid form-select-sm'
                                    value={selectedServiceId ?? ''}
                                    onChange={e => setSelectedServiceId(e.target.value ? Number(e.target.value) : undefined)}
                                >
                                    <option value=''>Выберите услугу</option>
                                    {(servicesData ?? []).map((s: any) => (
                                        <option key={s.id} value={s.id}>{s.title}</option>
                                    ))}
                                </select>
                            </div>
                        )}

                        <div className='sbw__footer'>
                            {mode === 'booking' && (
                                <button
                                    type='button'
                                    className='sbw__dateOnlyBtn'
                                    onClick={handleDateOnly}
                                >
                                    В лист ожидания
                                </button>
                            )}
                            <button
                                type='button'
                                className='sbw__selectBtn'
                                disabled={!canConfirm || booking}
                                onClick={handleConfirm}
                            >
                                {booking ? 'Запись...' : mode === 'booking' ? 'Записать' : 'Выбрать'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    )
}

export default ComponentScheduleBookingDate